import React, { useState, useEffect, useCallback, useMemo, ReactElement } from "react";

import { Close } from "@mui/icons-material";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import Button from "../../Form/Button";
import EmptySection from "./EmptySection";
import {
  Sidebar,
  SidebarList,
  SidebarListItem,
  SidebarTitle,
  Wrapper,
  Main,
  MainHeader,
  MainHeaderTitle,
  MainHeaderToggle,
  MainScreen,
  MainTitle,
  MainComponent,
  SidebarButtonsWrapper,
  SidebarButtonText,
  MainHeaderSubtitle,
  MainFooter,
} from "./styles";
import { ViewButtonItem, ViewTab } from "./types";

interface Props {
  sideTitle?: string;
  title: string;
  subtitle?: string;
  isLoading: boolean;
  tabs: ViewTab[];
  defaultTab: string;
  buttonsComponent?: ViewButtonItem[];
  isOpened: boolean;
  onClose: () => void;
  footerComponent?: ReactElement;
}

const SideView: React.FC<Props> = (props) => {
  const {
    tabs,
    isLoading,
    sideTitle,
    title,
    subtitle,
    buttonsComponent,
    isOpened,
    onClose,
    defaultTab,
    footerComponent,
  } = props;
  const { t: translate } = useTranslation();

  const [extraComponent, setExtraComponent] = useState<ViewButtonItem>();
  const [currentLink, setCurrentLink] = useState<string>(defaultTab);

  const navigation = useMemo(() => {
    return tabs.map((tab) => ({
      key: tab.key,
    }));
  }, [tabs]);

  const handleScrollIntoView = (id: string) => {
    const element = document.getElementById(id);

    setCurrentLink(id);

    element?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpened) {
      setTimeout(() => handleScrollIntoView(defaultTab), 500);
    }
  }, [isOpened, defaultTab]);

  const handleWheel = useCallback(
    (event) => {
      let nextIndex;

      if (event.wheelDelta <= 0) {
        nextIndex = navigation.findIndex((nav) => nav.key === currentLink) + 1;
      } else {
        nextIndex = navigation.findIndex((nav) => nav.key === currentLink) - 1;
      }

      if (nextIndex !== undefined && nextIndex <= navigation.length - 1 && nextIndex >= 0) {
        const nextPage = navigation[nextIndex]?.key;
        const nextPageTop = document?.getElementById(nextPage)?.getBoundingClientRect()?.top;
        const isToShow =
          nextPageTop && event.wheelDelta <= 0
            ? nextPageTop <= 200
            : nextPageTop && nextPageTop >= 95;

        if (nextPageTop && isToShow) setCurrentLink(nextPage);
      }
    },
    [currentLink]
  );

  const renderSection = (tab: ViewTab) => {
    if (tab.isEmpty) return <EmptySection />;

    return tab.render();
  };

  useEffect(() => {
    document?.getElementById("componentSection")?.addEventListener("wheel", handleWheel);

    return () => {
      document.getElementById("componentSection")?.removeEventListener("wheel", handleWheel);
    };
  });

  return (
    <Wrapper
      anchor="right"
      open={isOpened}
      onClose={() => {
        setCurrentLink(defaultTab);
        if (onClose) onClose();
      }}
      data-cy="sidebar-container"
    >
      <Sidebar data-cy="sidebar-navlist">
        <SidebarTitle>
          <span data-cy="sidebar-navlist-title">{sideTitle || translate("navigation")}</span>
        </SidebarTitle>
        <SidebarList data-cy="sidebar-navlist-list">
          {navigation.map((menuItem) => (
            <SidebarListItem
              id={`item-${menuItem.key}`}
              data-cy={`sidebar-navlist-item-${menuItem.key}`}
              is_loading={isLoading}
              className={`${menuItem.key === currentLink ? "active" : ""}`}
              key={menuItem.key}
              onClick={() => {
                if (isLoading) return;
                setExtraComponent(undefined);
                setTimeout(() => handleScrollIntoView(menuItem.key), 500);
              }}
            >
              <span>{tabs.find((tab) => tab.key === menuItem.key)?.label}</span>
            </SidebarListItem>
          ))}
        </SidebarList>
        <SidebarButtonsWrapper>
          {buttonsComponent?.map((button, index: number) => (
            <Button
              key={index}
              data-cy={button.buttonLabel}
              className={button.class}
              customColor={button.color}
              disabled={isLoading}
              onClick={() => {
                setExtraComponent(button);
                setCurrentLink("");
              }}
            >
              {button.icon}
              <SidebarButtonText>{button.buttonLabel}</SidebarButtonText>
            </Button>
          ))}
        </SidebarButtonsWrapper>
      </Sidebar>

      <Main data-cy="sidebar-page">
        <MainHeader data-cy="sidebar-page-header">
          <Stack direction={"column"}>
            <MainHeaderTitle data-cy="sidebarTitle">{title}</MainHeaderTitle>
            <MainHeaderSubtitle data-cy="sidebarSubtitle">{subtitle}</MainHeaderSubtitle>
          </Stack>
          <MainHeaderToggle data-cy="iconButtonClose" onClick={() => onClose()}>
            <Close color="inherit" />
          </MainHeaderToggle>
        </MainHeader>
        <MainScreen
          id="componentSection"
          data-cy="sidebar-page-screen"
          has_footer={!!footerComponent}
        >
          {extraComponent && (
            <div data-cy={`component-${extraComponent.titlePage}`}>
              <MainTitle data-cy="componentTitlePage">{extraComponent.titlePage}</MainTitle>
              <MainComponent data-cy="componentPage">
                {isLoading
                  ? extraComponent.renderLoading && extraComponent.renderLoading()
                  : extraComponent.renderComponent()}
              </MainComponent>
            </div>
          )}

          {!extraComponent &&
            tabs?.map((tab) => (
              <div id={tab.key} key={tab.key}>
                <MainTitle data-cy={`sidebar-page-screen-${tab.key}-title`}>{tab.label}</MainTitle>
                <MainComponent data-cy={`sidebar-page-screen-${tab.key}-component`}>
                  {isLoading || tab?.loading
                    ? tab.renderLoading && tab.renderLoading()
                    : renderSection(tab)}
                </MainComponent>
              </div>
            ))}
        </MainScreen>

        {footerComponent && (
          <MainFooter data-cy="sidebar-page-footer">{footerComponent}</MainFooter>
        )}
      </Main>
    </Wrapper>
  );
};

export default SideView;
